import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {useForm} from 'react-hook-form'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Alert,
    Button,
    Container,
    Stack,
    Text,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {CheckCircleIcon} from '@salesforce/retail-react-app/app/components/icons'
import Field from '../../components/field'
import PasswordRequirements from '../../components/forms/password-requirements'
import useUpdatePasswordFields from '../../components/forms/useUpdatePasswordFields'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {
    API_ERROR_MESSAGE,
    INVALID_TOKEN_ERROR,
    INVALID_TOKEN_ERROR_MESSAGE
} from '@salesforce/retail-react-app/app/constants'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

import {sendViewErrorEvent} from '../../utils/gtm-utils'

const ResetPasswordLanding = ({email, submitForm, setShouldShowHeading, clickSignIn = noop}) => {
    const {app: appConfig} = getConfig()
    const form = useForm()
    const {formatMessage} = useIntl()
    const fields = useUpdatePasswordFields({form})
    const password = form.watch('password')
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()

    const [isSuccess, setIsSuccess] = useState(false)

    const submit = async (values) => {
        form.clearErrors()
        try {
            const token = await getTokenWhenReady()
            const response = await fetch(`${appOrigin}/process-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    token,
                    form: {
                        email,
                        otp: values.otp,
                        newpassword: values.password
                    }
                })
            })

            if (!response.ok) {
                throw new Error('Failed to call OTP service')
            }

            const passwordData = await response.json()

            if (!passwordData || !passwordData.success) {
                const errMsg = passwordData?.error_message || 'Failed to update password'
                throw new Error(errMsg)
            } else {
                setIsSuccess(true)
                setShouldShowHeading(false)
            }
        } catch (error) {
            let errorMessage = error.message
            let gtmMsg

            try {
                const errorData = await error.response?.json()
                errorMessage = errorData?.message || errorMessage
                gtmMsg = errorMessage
            } catch (_) {
                // Do nothing
            }

            if (errorMessage === 'invalid OTP') {
                errorMessage =
                    'Invalid password. Please try again or send a new temporary password.'
                form.setError('otp', {
                    type: 'manual',
                    message: formatMessage({
                        defaultMessage:
                            'Invalid password. Please try again or send a new temporary password.',
                        id: 'reset_password_form.error.invalid_otp'
                    })
                });
            } else if (/password.*policy/i.test(errorMessage)) {
                gtmMsg = 'Current and new passwords cannot be the same.'
                form.setError('password', {
                    type: 'manual',
                    message: formatMessage({
                        defaultMessage: 'Current and new passwords cannot be the same.',
                        id: 'reset_password_form.error.password_policy'
                    })
                });
            } else if (INVALID_TOKEN_ERROR.test(errorMessage)) {
                gtmMsg = INVALID_TOKEN_ERROR_MESSAGE.defaultMessage
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage(INVALID_TOKEN_ERROR_MESSAGE)
                });
            } else {
                gtmMsg = API_ERROR_MESSAGE.defaultMessage
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage(API_ERROR_MESSAGE)
                });
            }
            // GTM Modification
            sendViewErrorEvent(gtmMsg)
        }
    }

    if (isSuccess) {
        return (
            <Stack>
                <Stack spacing={{base: 7, lg: 6}} mb={8}>
                    <CheckCircleIcon h={8} w={8} />
                    <Text
                        fontSize="md"
                        fontWeight="bold"
                        textTransform="uppercase"
                        lineHeight="20px"
                    >
                        <FormattedMessage
                            defaultMessage="GOOD TO GO!"
                            id="reset_password_form.title.good_to_go"
                        />
                    </Text>
                    <Text fontSize={{base: 'md', lg: 'sm'}} lineHeight={{base: 6, lg: '18px'}}>
                        <FormattedMessage
                            defaultMessage="Your password has been successfully changed. Please use your new password to log in!"
                            id="reset_password_form.info.password_changed"
                        />
                    </Text>
                </Stack>

                <Button
                    type="button"
                    textTransform="uppercase"
                    onClick={clickSignIn}
                    lineHeight="20px"
                    fontSize="md"
                >
                    <FormattedMessage
                        defaultMessage="Sign In"
                        id="reset_password_form.action.sign_in"
                    />
                </Button>
            </Stack>
        )
    }

    return (
        <Stack spacing={2}>
            <Text
                fontSize="md"
                lineHeight="20px"
                mb={4}
                fontWeight="bold"
                textTransform="uppercase"
            >
                <FormattedMessage
                    defaultMessage="Instructions sent"
                    id="reset_password_page.email_sent"
                />
            </Text>
            <Text fontSize="sm" lineHeight="18px">
                <FormattedMessage
                    defaultMessage="Check your email that we sent to <b>{email}</b>"
                    id="reset_password_page.email_sent.text"
                    values={{
                        email,
                        b: (chunks) => <b>{chunks}</b>
                    }}
                />
            </Text>

            <Text fontSize="xs" lineHeight="15px">
                <FormattedMessage
                    defaultMessage="If you don’t receive an email within 5 minutes, check your junk folder or contact <a>Customer Service</a>."
                    id="reset_password_page.email_sent.help"
                    values={{
                        a: (chunks) => <Link
                            href={appConfig?.CUSTOMER_SERVICE_URL}
                            target='_blank'
                            rel="noopener noreferrer"
                            textDecoration="underline">{chunks}</Link>
                    }}
                />
            </Text>

            <Text fontSize="xs">
                <FormattedMessage
                    defaultMessage="Didn’t receive it? <a>Send me a new one</a>"
                    id="reset_password_page.email_sent.retry"
                    values={{
                        a: (chunks) => (
                            <Link
                                as="button"
                                onClick={() => submitForm?.({email})}
                                textDecoration="underline"
                            >
                                {chunks}
                            </Link>
                        )
                    }}
                />
            </Text>
            <Stack justify="center" align="center" spacing={6}>
                <Container variant="form">
                    <form onSubmit={form.handleSubmit(submit)} noValidate>
                        <Stack spacing={6}>
                            {form.formState.errors?.global && (
                                <Alert data-testid="password-update-error" status="error">
                                    <AlertIcon color="red.500" boxSize={4} />
                                    <Text fontSize="sm" ml={3}>
                                        {form.formState.errors.global.message}
                                    </Text>
                                </Alert>
                            )}
                            <Stack spacing={3} pb={2}>
                                <Field {...fields.otp} isResetPassword={true} />
                                <Field {...fields.password} isResetPassword={true} />
                                <PasswordRequirements value={password} />
                                <Field {...fields.passwordConfirm} isResetPassword={true} />
                            </Stack>
                            <Button
                                type="submit"
                                isLoading={form.formState.isSubmitting}
                                isDisabled={
                                    !form.watch('otp') ||
                                    !form.watch('password') ||
                                    !form.watch('passwordConfirm')
                                }
                                fontSize="md"
                                lineHeight="20px"
                            >
                                <FormattedMessage
                                    defaultMessage="Set Password"
                                    id="reset_password_form.button.set_password"
                                />
                            </Button>
                        </Stack>
                    </form>
                </Container>
            </Stack>
        </Stack>
    )
}

ResetPasswordLanding.getTemplateName = () => 'reset-password-landing'

ResetPasswordLanding.propTypes = {
    email: PropTypes.string,
    submitForm: PropTypes.func,
    setShouldShowHeading: PropTypes.func,
    clickSignIn: PropTypes.func
}

export default ResetPasswordLanding
